import { DAY_MS } from "@jackedlogs/shared";
import type {
  Max,
  RepsIntensityMatrix,
  TimedSessionResult,
} from "@jackedlogs/shared";

// Pure helpers behind the Tier-2 progress panels (lifted from mobile with
// the panels). The matrix reductions and the pickable / stalest
// aggregations live here so they can be unit-tested. Display names stay
// render-time concerns (FK→catalog), so these return the deleted-row
// snapshot only.

export const sumMatrix = (m: RepsIntensityMatrix): number =>
  m.reduce((acc, row) => acc + row.reduce((a, c) => a + c, 0), 0);

export const maxCell = (m: RepsIntensityMatrix): number => {
  let max = 0;
  for (const row of m) for (const c of row) if (c > max) max = c;
  return max;
};

// --- Heatmap cell colouring -------------------------------------------------
// The intensity heatmap colours each cell by its intensity COLUMN — the four
// %1RM zones (<50% / 50-70% / 70-85% / 85%+) map 1:1 onto the shared
// intensityScale bands (work / moderate / heavy / max), and opacity ramps with
// the set count. The white-on-white (dark) and black-on-black (light) reports
// (inbox b5b69fc9 / bcb8f1e3) came from drawing the count in the theme ink over
// a high-alpha ink-ramp cell: text and fill were the SAME colour. We composite
// the band fill over the surface at the cell's alpha and pick whichever
// candidate text colour clears AA (4.5:1), so the number is always legible
// regardless of theme, hue, or count.
type Rgb = { r: number; g: number; b: number };

function hexToRgb(hex: string): Rgb {
  const h = /^#?([0-9a-f]{6})$/i.exec(hex.trim())?.[1];
  if (!h) return { r: 10, g: 12, b: 13 };
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

// alpha-composite an `fg` colour at `alpha` over an opaque `bg`.
function compositeOver(fg: Rgb, alpha: number, bg: Rgb): Rgb {
  const mix = (f: number, b: number) => Math.round(f * alpha + b * (1 - alpha));
  return { r: mix(fg.r, bg.r), g: mix(fg.g, bg.g), b: mix(fg.b, bg.b) };
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

// Set-count → fill opacity. count 0 is transparent (the caller renders the
// empty surface instead). Mirrors the original ramp: a faint floor so a single
// set still reads, climbing to full at the busiest cell.
export function cellAlpha(count: number, peak: number): number {
  if (count <= 0) return 0;
  const p = peak > 0 ? peak : 1;
  return 0.15 + (count / p) * 0.85;
}

export type HeatCellColors = { background: string; text: string };

// Resolve {background, text} for one heatmap cell. `candidates` are the
// preferred text colours in order (the band's purpose-built `on` first, then
// theme ink / inkOnBrand for the faint-alpha end where the cell is mostly
// surface): the first that clears AA (4.5:1) wins. Pure black + white are
// appended as last-resort fallbacks — against ANY background at least one of
// them clears ~4.58:1, so the number can never end up below AA (the dead-zone
// near mid-luminance where every theme candidate falls short, e.g. a busy
// `heavy` cell). Aesthetics win first; the absolutes only catch the gap.
const AA_FALLBACKS = ["#000000", "#ffffff"] as const;

export function heatCellColors(
  bandFillHex: string,
  alpha: number,
  surfaceHex: string,
  candidates: readonly string[],
): HeatCellColors {
  const bg = compositeOver(hexToRgb(bandFillHex), alpha, hexToRgb(surfaceHex));
  const background = `rgb(${bg.r},${bg.g},${bg.b})`;
  let best = candidates[0] ?? AA_FALLBACKS[0];
  let bestRatio = -1;
  for (const c of [...candidates, ...AA_FALLBACKS]) {
    const ratio = contrastRatio(hexToRgb(c), bg);
    if (ratio >= 4.5) return { background, text: c };
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = c;
    }
  }
  return { background, text: best };
}

// Pickable exercises for the intensity heatmap = anything the user has
// both logged AND has a MAX recorded for (the matrix denominator needs the
// MAX). Sorted most-recently-logged first.
export type IntensityPick = {
  slug: string;
  snapshot: string | undefined;
  lastMs: number;
  maxKg: number;
};

export function computeIntensityPickable(
  results: TimedSessionResult[],
  maxes: ReadonlyArray<Max>,
): IntensityPick[] {
  const maxBySlug = new Map(maxes.map((m) => [m.exerciseId, m.valueKg]));
  const seen = new Map<string, { snapshot?: string; lastMs: number }>();
  for (const ts of results) {
    for (const er of ts.result.exerciseResults) {
      if (er.setCount === 0) continue;
      if (!maxBySlug.has(er.exerciseSlug)) continue;
      const cur = seen.get(er.exerciseSlug) ?? {
        snapshot: er.exerciseName,
        lastMs: 0,
      };
      cur.lastMs = Math.max(cur.lastMs, ts.startedAtMs);
      seen.set(er.exerciseSlug, cur);
    }
  }
  return Array.from(seen.entries())
    .map(([s, v]) => ({
      slug: s,
      snapshot: v.snapshot,
      lastMs: v.lastMs,
      maxKg: maxBySlug.get(s) ?? 0,
    }))
    .sort((a, b) => b.lastMs - a.lastMs);
}

// Stalest exercises = max days-since-last among exercises ever logged,
// stalest first, capped. Derived from SessionResult.exerciseResults so no
// separate LogSet query is needed.
// PG-1 period selector: `windowDays` bounds the recent set-count (was fixed
// 28d); `daysSince` is inherently un-windowed (how stale, regardless of the
// selected period). Default 28 keeps the pre-selector behaviour identical.
export type StaleExercise = {
  slug: string;
  snapshot: string | undefined;
  daysSince: number;
  setsInWindow: number;
};

export function computeStalestExercises(
  results: TimedSessionResult[],
  nowMs: number,
  windowDays = 28,
  limit = 8,
): StaleExercise[] {
  const lastSeen = new Map<
    string,
    { snapshot: string | undefined; lastMs: number; setsInWindow: number }
  >();
  const cutoffWindow = nowMs - windowDays * DAY_MS;
  for (const ts of results) {
    const ms = ts.startedAtMs;
    for (const er of ts.result.exerciseResults) {
      if (er.setCount === 0) continue;
      const cur = lastSeen.get(er.exerciseSlug) ?? {
        snapshot: er.exerciseName,
        lastMs: 0,
        setsInWindow: 0,
      };
      cur.lastMs = Math.max(cur.lastMs, ms);
      if (ms >= cutoffWindow) cur.setsInWindow += er.setCount;
      lastSeen.set(er.exerciseSlug, cur);
    }
  }
  return Array.from(lastSeen.entries())
    .map(([slug, v]) => ({
      slug,
      snapshot: v.snapshot,
      daysSince: Math.floor((nowMs - v.lastMs) / DAY_MS),
      setsInWindow: v.setsInWindow,
    }))
    .sort((a, b) => b.daysSince - a.daysSince)
    .slice(0, limit);
}
